import React, { AriaRole, ForwardedRef, forwardRef, ReactNode } from 'react';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/radio/radio.module.css';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';
import {
  CheckboxContext,
  SlotProps,
  useContextProps,
} from 'react-aria-components';

export type RadioBoxProps = GlobalDomAttributes &
  SapphireStyleProps &
  SlotProps & {
    elementType?: 'div' | 'label';
    size?: 'lg' | 'md';
    isDisabled?: boolean;
    isHovered?: boolean;
    isFocusVisible?: boolean;
    isPressed?: boolean;
    isSelected?: boolean;
    label?: ReactNode;
    children?: ReactNode;
    role?: AriaRole;
  };

/**
 * Internal "dumb" component which allows one to render the radio box. This
 * does not come with any radio-related functionality. It is just the plain UI
 * element.
 */
export const RadioBox = forwardRef(function RadioBox(
  props: RadioBoxProps,
  ref: ForwardedRef<HTMLLabelElement>
) {
  [props, ref] = useContextProps(props, ref, CheckboxContext);
  const {
    elementType: CustomElement = 'div',
    size,
    isDisabled,
    isHovered,
    isFocusVisible,
    isPressed,
    isSelected,
    label,
    role,
    children,
    slot,
    ...otherProps
  } = props;
  const { styleProps, filteredProps } = useSapphireStyleProps(otherProps);
  const ariaProps =
    role === 'checkbox' ? { role, 'aria-checked': isSelected } : { role };

  return (
    <CustomElement
      {...filteredProps}
      {...ariaProps}
      ref={ref as ForwardedRef<any>}
      className={clsx(
        styles['sapphire-radio'],
        {
          [styles['sapphire-radio--checked']]: isSelected,
          [styles['sapphire-radio--md']]: size === 'md',
          [styles['is-hover']]: isHovered,
          [styles['is-focus']]: isFocusVisible,
          [styles['is-active']]: isPressed,
        },
        styles['js-focus'],
        styleProps.className
      )}
      style={styleProps.style}
    >
      {children}
      <span className={clsx(styles['sapphire-radio__box'])} />
      {label && (
        <span className={clsx(styles['sapphire-radio__label'])}>{label}</span>
      )}
    </CustomElement>
  );
});
